import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import * as action from '../Action/Categories/Categories_Action';

const GenreSeries = ({ id, categoryName }) => {
    const dispatch = useDispatch();
    const tableRef = useRef(null);
    const dataTableInstanceRef = useRef(null);

    const [rankingMode, setRankingMode] = useState(false);
    const [rankItems, setRankItems] = useState([]);
    const [rankLoading, setRankLoading] = useState(false);
    const [savingRank, setSavingRank] = useState(false);
    const [count, setCount] = useState(0);

    const dragSrcIdxRef = useRef(null);

    const categoryId = parseInt(id, 10);

    const fetchAllForRanking = useCallback(() => {
        setRankLoading(true);
        dispatch(action.getCategorySeriesList({ category_id: categoryId, page: 1, limit: 1000 }))
            .then((res) => {
                const rd = res.responseDetails || res.data || res;
                const rows = rd.rows || rd.data || (Array.isArray(rd) ? rd : []);
                const sorted = [...rows].sort((a, b) => {
                    if (a.category_rank == null && b.category_rank == null) return 0;
                    if (a.category_rank == null) return 1;
                    if (b.category_rank == null) return -1;
                    return a.category_rank - b.category_rank;
                });
                setRankItems(sorted);
            })
            .catch(() => toast.error('Failed to load series for ranking.'))
            .finally(() => setRankLoading(false));
    }, [dispatch, categoryId]);

    const enterRankingMode = () => {
        if (dataTableInstanceRef.current) {
            try { dataTableInstanceRef.current.destroy(); } catch (e) {}
            dataTableInstanceRef.current = null;
        }
        setRankingMode(true);
        fetchAllForRanking();
    };

    const exitRankingMode = useCallback(() => {
        setRankingMode(false);
        setRankItems([]);
    }, []);

    const saveRanking = () => {
        setSavingRank(true);
        const ranks = rankItems.map((item, index) => ({ series_id: item.id, rank: index + 1 }));
        dispatch(action.updateCategorySeriesRanks({ category_id: categoryId, ranks }))
            .then(() => {
                toast.success('Series order saved successfully!');
                exitRankingMode();
            })
            .catch((err) => {
                toast.error(err?.responseMessage || err?.message || 'Failed to save order. Please try again.');
            })
            .finally(() => setSavingRank(false));
    };

    const onDragStart = (e, index) => {
        dragSrcIdxRef.current = index;
        e.dataTransfer.effectAllowed = 'move';
        e.currentTarget.style.opacity = '0.5';
    };

    const onDragEnd = (e) => {
        e.currentTarget.style.opacity = '1';
    };

    const onDragOver = (e, index) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        const src = dragSrcIdxRef.current;
        if (src === null || src === index) return;
        setRankItems(prev => {
            const next = [...prev];
            const [removed] = next.splice(src, 1);
            next.splice(index, 0, removed);
            dragSrcIdxRef.current = index;
            return next;
        });
    };

    const onDrop = (e) => { e.preventDefault(); };

    useEffect(() => {
        if (rankingMode) return;

        let tableRefCurrent = tableRef.current;
        const $ = window.$ || window.jQuery;
        let currentSize = window.innerWidth <= 450 ? 'mobile' : 'desktop';

        function setPaginationOptions() {
            if (currentSize === 'mobile') {
                $.fn.dataTable.ext.pager.numbers_length = 0;
                return 'simple';
            } else {
                $.fn.dataTable.ext.pager.numbers_length = 5;
                return 'simple_numbers';
            }
        }

        const initDataTable = () => {
            const pagingType = setPaginationOptions();
            if ($ && tableRefCurrent) {
                dataTableInstanceRef.current = $(tableRefCurrent).DataTable({
                    processing: true,
                    serverSide: true,
                    scrollX: true,
                    pagingType,
                    ajax: (dataTablesParams, callback) => {
                        const params = {
                            category_id: categoryId,
                            search: dataTablesParams.search.value,
                            page: Math.floor(dataTablesParams.start / dataTablesParams.length) + 1,
                            limit: dataTablesParams.length,
                        };
                        dispatch(action.getCategorySeriesList(params))
                            .then((res) => {
                                const rd = res.responseDetails || res.data || res;
                                const rows = rd.rows || rd.data || (Array.isArray(rd) ? rd : []);
                                const total = rd.total || rd.totalRecords || 0;
                                setCount(total);
                                callback({
                                    draw: dataTablesParams.draw,
                                    data: rows,
                                    recordsTotal: total,
                                    recordsFiltered: rd.recordsFiltered || total,
                                });
                            })
                            .catch((err) => {
                                console.error('GenreSeries list error:', err);
                                callback({ draw: dataTablesParams.draw, data: [], recordsTotal: 0, recordsFiltered: 0 });
                            });
                    },
                    destroy: true,
                    columns: [
                        {
                            data: 'thumbnail',
                            title: 'Thumbnail',
                            orderable: false,
                            render: (val) => val
                                ? `<img src="${val}" alt="" style="width:50px;height:70px;object-fit:cover;border-radius:4px;" loading="lazy" />`
                                : `<div style="width:50px;height:70px;background:#2a2a3d;border-radius:4px;display:flex;align-items:center;justify-content:center;"><i class="fas fa-image" style="color:#555;font-size:18px;"></i></div>`
                        },
                        { data: 'title', title: 'Title' },
                        {
                            data: 'actual_episode_count',
                            title: 'Episodes',
                            orderable: false,
                            render: (val) => `<span style="font-weight:600;">${val ?? 0}</span>`
                        },
                        {
                            data: 'category_rank',
                            title: 'Rank',
                            orderable: false,
                            render: (val) => val != null
                                ? `<span class="badge" style="background:var(--first-color);color:#fff;padding:3px 8px;border-radius:12px;">#${val}</span>`
                                : `<span style="color:#666;font-size:12px;">—</span>`
                        },
                        {
                            data: 'is_active',
                            title: 'Status',
                            orderable: false,
                            render: (val) => val
                                ? `<span class="badge" style="background:#10b981;color:#fff;padding:3px 10px;border-radius:12px;">Active</span>`
                                : `<span class="badge" style="background:#ef4444;color:#fff;padding:3px 10px;border-radius:12px;">Inactive</span>`
                        },
                    ]
                });

                $(tableRefCurrent).on('preXhr.dt', function () {
                    const processingEl = $('.dataTables_processing');
                    const visibleCols = $(tableRefCurrent).find('thead th:visible').length;
                    if (processingEl.length) {
                        $(tableRefCurrent).find('tbody').html(
                            `<tr class="dt-temp-loader"><td colspan="${visibleCols}" class="text-center"><div class="dataTables_processing card" style="display:block;margin:0 auto">${processingEl.html()}</div></td></tr>`
                        );
                    }
                });

                $(tableRefCurrent).on('xhr.dt', function () {
                    $(tableRefCurrent).find('tbody .dt-temp-loader').remove();
                });
            }
        };

        initDataTable();

        const handleResize = () => {
            const newSize = window.innerWidth <= 450 ? 'mobile' : 'desktop';
            if (newSize !== currentSize) {
                currentSize = newSize;
                if (dataTableInstanceRef.current) {
                    try { dataTableInstanceRef.current.destroy(); } catch (e) {}
                    dataTableInstanceRef.current = null;
                }
                initDataTable();
            }
        };
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            if (dataTableInstanceRef.current) {
                try { dataTableInstanceRef.current.destroy(); } catch (e) {}
                dataTableInstanceRef.current = null;
            }
        };
    }, [dispatch, categoryId, rankingMode]);

    return (
        <section className="genres-section section">
            <div className="card">
                <div className="card-header">
                    <div className="page-title w-100 d-flex align-items-center justify-content-between flex-wrap gap-2">
                        <div className="d-flex align-items-center gap-3">
                            <Link
                                to="/genres"
                                style={{
                                    color: 'var(--first-color)',
                                    textDecoration: 'none',
                                    fontWeight: 500,
                                    fontSize: '14px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}
                            >
                                <i className="fas fa-arrow-left"></i> Categories
                            </Link>
                            <span style={{ color: '#555' }}>/</span>
                            <h4 style={{ margin: 0 }}>
                                {categoryName || 'Category'} Series
                                {!rankingMode && (
                                    <span style={{ fontSize: '14px', fontWeight: 400, marginLeft: '6px', color: '#888' }}>
                                        ({count})
                                    </span>
                                )}
                            </h4>
                        </div>

                        <div className="d-flex gap-2">
                            {!rankingMode ? (
                                <button
                                    className="btn"
                                    onClick={enterRankingMode}
                                    style={{ border: '1px solid var(--first-color)', color: 'var(--first-color)', background: 'transparent', fontWeight: 500 }}
                                >
                                    <i className="fas fa-sort" style={{ marginRight: '6px' }}></i>
                                    Manage Order
                                </button>
                            ) : (
                                <>
                                    <button className="btn btn-secondary" onClick={exitRankingMode} disabled={savingRank}>
                                        Cancel
                                    </button>
                                    <button className="btn upgrade-btn" onClick={saveRanking} disabled={savingRank || rankLoading}>
                                        {savingRank
                                            ? <><span className="spinner-border spinner-border-sm me-1" style={{ width: 14, height: 14 }} /> Saving...</>
                                            : <><i className="fas fa-save" style={{ marginRight: 6 }}></i>Save Order</>
                                        }
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                <div className="card-body">
                    {rankingMode ? (
                        <>
                            <p style={{ color: '#888', fontSize: '13px', marginBottom: '12px' }}>
                                <i className="fas fa-info-circle" style={{ marginRight: '6px', color: 'var(--first-color)' }}></i>
                                Drag rows using the <strong>⠿</strong> handle to reorder. This rank applies only to <strong>{categoryName || 'this category'}</strong>. Click <strong>Save Order</strong> when done.
                            </p>
                            {rankLoading ? (
                                <div className="text-center py-4">
                                    <div className="spinner-border" style={{ color: 'var(--first-color)' }} role="status" />
                                </div>
                            ) : (
                                <table className="table table-striped w-100" style={{ tableLayout: 'fixed' }}>
                                    <thead>
                                        <tr>
                                            <th style={{ width: 40 }}></th>
                                            <th style={{ width: 50 }}>#</th>
                                            <th style={{ width: 60 }}>Thumb</th>
                                            <th>Title</th>
                                            <th style={{ width: 80 }}>Episodes</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rankItems.map((item, index) => (
                                            <tr
                                                key={item.id}
                                                draggable
                                                onDragStart={(e) => onDragStart(e, index)}
                                                onDragEnd={onDragEnd}
                                                onDragOver={(e) => onDragOver(e, index)}
                                                onDrop={onDrop}
                                                style={{ cursor: 'default' }}
                                            >
                                                <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                                    <span
                                                        style={{ cursor: 'grab', fontSize: '18px', color: 'var(--first-color, #e91e63)', userSelect: 'none' }}
                                                        title="Drag to reorder"
                                                    >
                                                        ⠿
                                                    </span>
                                                </td>
                                                <td style={{ color: '#888', fontSize: '13px', verticalAlign: 'middle' }}>{index + 1}</td>
                                                <td style={{ verticalAlign: 'middle' }}>
                                                    {item.thumbnail
                                                        ? <img src={item.thumbnail} alt="" style={{ width: 40, height: 56, objectFit: 'cover', borderRadius: 4 }} loading="lazy" />
                                                        : <div style={{ width: 40, height: 56, background: '#2a2a3d', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                            <i className="fas fa-image" style={{ color: '#555', fontSize: 14 }}></i>
                                                          </div>
                                                    }
                                                </td>
                                                <td style={{ verticalAlign: 'middle' }}>{item.title}</td>
                                                <td style={{ verticalAlign: 'middle', color: '#888', fontSize: '13px' }}>{item.actual_episode_count ?? 0} ep</td>
                                            </tr>
                                        ))}
                                        {rankItems.length === 0 && (
                                            <tr>
                                                <td colSpan={5} className="text-center py-4" style={{ color: '#888' }}>No series found in this category.</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            )}
                        </>
                    ) : (
                        <table
                            ref={tableRef}
                            className="genres-table user-table table table-striped w-100 dataTable no-footer"
                            role="grid"
                        />
                    )}
                </div>
            </div>
        </section>
    );
};

export default GenreSeries;
